import { useState, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, Pencil, Save, X, AlertCircle, Clock, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const DATE_FIELDS = [
  { key: "inspection_date", completedKey: "inspection_completed", label: "Inspection Date" },
  { key: "appraisal_date", completedKey: "appraisal_completed", label: "Appraisal Date" },
  { key: "financing_deadline", completedKey: "financing_completed", label: "Financing Deadline" },
  { key: "closing_date", completedKey: "closing_completed", label: "Closing Date" },
];

const formatDate = (val) => {
  if (!val) return null;
  return new Date(val + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const daysUntil = (val) => {
  if (!val) return null;
  const target = new Date(val + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
};

const relativeText = (days) => {
  if (days === null) return null;
  if (days === 0) return "today";
  if (days === 1) return "in 1 day";
  if (days > 1) return `in ${days} days`;
  if (days === -1) return "1 day ago";
  return `${Math.abs(days)} days ago`;
};

const urgencyState = (days) => {
  if (days === null) return "default";
  if (days <= 0) return "urgent";
  if (days <= 5) return "upcoming";
  return "default";
};

export const KeyDatesCard = ({ transaction, onUpdate, isAgent }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Summary: most urgent INCOMPLETE date only
  const summaryInfo = useMemo(() => {
    let mostUrgent = null;
    for (const field of DATE_FIELDS) {
      const val = transaction[field.key];
      const completed = !!transaction[field.completedKey];
      if (!val || completed) continue;
      const days = daysUntil(val);
      if (days === null) continue;
      if (!mostUrgent || days < mostUrgent.days) {
        mostUrgent = { days, label: field.label };
      }
    }
    return mostUrgent;
  }, [transaction]);

  const openEdit = () => {
    setForm({
      inspection_date: transaction.inspection_date || "",
      appraisal_date: transaction.appraisal_date || "",
      financing_deadline: transaction.financing_deadline || "",
      closing_date: transaction.closing_date || "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const update = {
        inspection_date: form.inspection_date || null,
        appraisal_date: form.appraisal_date || null,
        financing_deadline: form.financing_deadline || null,
        closing_date: form.closing_date || null,
      };
      const { error } = await supabase.from("transactions").update(update).eq("id", transaction.id);
      if (error) throw error;
      onUpdate(update);
      setEditing(false);
      toast.success("Key dates saved");
    } catch (err) {
      console.error("[KeyDates] Save error:", err);
      toast.error("Failed to save dates");
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (field) => {
    const newVal = !transaction[field.completedKey];
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ [field.completedKey]: newVal })
        .eq("id", transaction.id);
      if (error) throw error;
      onUpdate({ [field.completedKey]: newVal });
    } catch (err) {
      console.error("[KeyDates] Toggle error:", err);
      toast.error("Failed to update");
    }
  };

  let summaryLine = null;
  if (summaryInfo) {
    const { days, label } = summaryInfo;
    if (days < 0) {
      summaryLine = { text: `Overdue: ${label} was due ${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`, urgency: "urgent" };
    } else if (days === 0) {
      summaryLine = { text: `${label} is today`, urgency: "urgent" };
    } else {
      summaryLine = { text: `Next deadline: ${label} in ${days} day${days !== 1 ? "s" : ""}`, urgency: days <= 5 ? "upcoming" : "default" };
    }
  }

  return (
    <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6 my-6" data-testid="key-dates-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CalendarDays className="w-[18px] h-[18px] text-slate-400" strokeWidth={1.75} />
          Key Dates
        </h3>
        {isAgent && !editing && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500 hover:text-slate-700" onClick={openEdit} data-testid="key-dates-edit-btn">
            <Pencil className="w-3.5 h-3.5 mr-1.5" />
            Edit Dates
          </Button>
        )}
      </div>

      {/* Summary line — incomplete dates only */}
      {!editing && summaryLine && (
        <div className={`flex items-center gap-2 mb-4 text-[13px] ${
          summaryLine.urgency === "urgent" ? "text-red-600" :
          summaryLine.urgency === "upcoming" ? "text-amber-600" : "text-slate-500"
        }`} data-testid="key-dates-summary">
          {summaryLine.urgency === "urgent" ? <AlertCircle className="w-3.5 h-3.5 shrink-0" /> : <Clock className="w-3.5 h-3.5 shrink-0" />}
          <span className="font-medium">{summaryLine.text}</span>
        </div>
      )}
      {!editing && !summaryLine && <div className="mb-4" />}

      {editing ? (
        <div className="space-y-3 mt-4" data-testid="key-dates-form">
          {DATE_FIELDS.map((field) => (
            <div key={field.key} className="flex items-center justify-between gap-4">
              <label className="text-[13px] text-slate-400 tracking-wide shrink-0 w-[140px]">{field.label}</label>
              <Input
                type="date"
                className="max-w-[180px] h-9 text-sm"
                value={form[field.key] || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                data-testid={`key-dates-input-${field.key}`}
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setEditing(false)}>
              <X className="w-3.5 h-3.5 mr-1" />Cancel
            </Button>
            <Button size="sm" className="h-8 text-xs" onClick={handleSave} disabled={saving} data-testid="key-dates-save-btn">
              <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="key-dates-display">
          {DATE_FIELDS.map((field) => {
            const val = transaction[field.key];
            const completed = !!transaction[field.completedKey];
            const days = daysUntil(val);
            const urgency = completed ? "default" : urgencyState(days);
            const relative = completed ? null : relativeText(days);

            return (
              <div key={field.key} className="flex items-center justify-between py-3.5 first:pt-0 last:pb-0 gap-3" data-testid={`key-date-row-${field.key}`}>
                {/* Left: completion toggle + label */}
                <div className="flex items-center gap-2.5 min-w-0">
                  {isAgent && val ? (
                    <button
                      onClick={() => toggleComplete(field)}
                      className="shrink-0 transition-colors"
                      title={completed ? "Mark incomplete" : "Mark complete"}
                      data-testid={`key-date-toggle-${field.key}`}
                    >
                      {completed ? (
                        <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500" />
                      ) : (
                        <Circle className="w-[18px] h-[18px] text-slate-300 hover:text-slate-400" />
                      )}
                    </button>
                  ) : val && completed ? (
                    <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500 shrink-0" />
                  ) : null}
                  <span className={`text-[13px] tracking-wide ${
                    completed ? "text-emerald-600 line-through" :
                    urgency === "urgent" ? "text-red-500 font-medium" :
                    urgency === "upcoming" ? "text-amber-600" : "text-slate-400"
                  }`}>{field.label}</span>
                </div>

                {/* Right: date + relative time or status */}
                <span className={`text-sm text-right shrink-0 ${
                  !val ? "text-slate-400" :
                  completed ? "text-emerald-600" :
                  urgency === "urgent" ? "font-semibold text-red-600" :
                  urgency === "upcoming" ? "font-medium text-amber-700" : "font-medium text-slate-800"
                }`}>
                  {val ? (
                    completed ? (
                      <span className="flex items-center gap-1.5 justify-end">
                        {formatDate(val)}
                        <span className="text-xs text-emerald-500">Completed</span>
                      </span>
                    ) : (
                      <>
                        {formatDate(val)}
                        {relative && <span className={`ml-1.5 text-xs ${
                          urgency === "urgent" ? "text-red-500" :
                          urgency === "upcoming" ? "text-amber-500" : "text-slate-400"
                        }`}>({relative})</span>}
                      </>
                    )
                  ) : "Not set"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
